/**
 * Chart-type conversions, kept out of registry.ts on purpose.
 *
 * This is pure data with no Highcharts dependency, so anything that only
 * needs to know "what can this chart turn into" — the builder's per-widget
 * settings (charts/widgetConfig.ts), the chart context menu — can import it
 * without pulling in every renderer and the Highcharts bundle behind them.
 *
 * registry.ts re-exports getConvertibleTargets, so existing importers are
 * unaffected.
 */

// ---------------------------------------------------------------------------
// CHART_CONVERSIONS — single source of truth for all chart-type conversions.
//
// Each key lists the chart types a user can switch to from that source.
// "table" means the built-in table view (not the data_table widget).
//
// Comment legend:
//   ↔  bidirectional  — both A→B and B→A are declared (symmetric round-trip)
//   →  one-way only   — the reverse direction is intentionally absent
//
// To add a new conversion: append the target to the source's array.
// For a bidirectional pair: add to BOTH sides and mark both ↔.
// Chart types with no entry have zero conversions (stacked_bar, force_bar, etc.).
// ---------------------------------------------------------------------------
const CHART_CONVERSIONS: Record<string, string[]> = {
  // ↔ bar ↔ pie — identical data shape, full round-trip
  // → bar → single_bar — same {name, value, percentage} shape, stacked into one column
  bar: ["pie", "single_bar", "table"],
  pie: ["bar", "table"],

  // ↔ single_bar ↔ bar / → table — same {name, value, percentage} shape as pie
  single_bar: ["bar", "table"],

  // → pie_drilldown → bar  (top-level slices map to bar categories)
  // → pie_drilldown → table
  pie_drilldown: ["bar", "table"],

  // → funnel → bar  (same {category, count} shape)
  //   bar → funnel NOT offered — funnel is always the originating source
  // funnel: ["bar"],

  // ↔ map ↔ bar, ↔ map ↔ pie — state rows map to category/count for both targets
  //   bar → map and pie → map NOT listed — they originate from map, not the reverse
  map: ["bar", "pie", "table"],

  // → data_table → bar / pie  (raw API table visualised as a chart on demand)
  //   bar / pie → data_table NOT offered — data_table is always the originating source
  // data_table: ["bar", "pie"],

  // → table-only (no meaningful chart↔chart conversion for these types)
  multi_line: ["table"],
  special_multi_line: ["table"],
  line: ["table"],
  // tiered_bar: ["table"],
  quadrant_scatter: ["table"],

  // No conversions — complex / composite data that doesn't reduce to flat rows:
  //   stacked_bar     — multi-series grouped/stacked
  //   time_series_bar — xAxis is dates, not plain categories
  //   force_bar       — nested hierarchy, can't flatten to rows
  //   line_with_bar   — dual-axis combo
};

// Returns the chart types a user can convert to from `chartType`.
// Reads from CHART_CONVERSIONS — the single place to configure all conversions.
export function getConvertibleTargets(chartType: string): string[] {
  return CHART_CONVERSIONS[chartType] ?? [];
}
